import { Zap, PenLine } from 'lucide-react';

/**
 * The single source of truth for the SIGNAL vs ANALYSIS visual marker.
 *
 * SIGNAL  = actionable trade (entry/SL/TP, settles, counts toward stats) → indigo + ⚡
 * ANALYSIS = chart + thesis only (no levels, never counted)              → amber + ✎
 *
 * Colors match the app's established semantics (indigo = accent/system, amber = win/insight).
 * `undefined` ideaType is treated as SIGNAL, matching the DB default and every read API (`?? 'SIGNAL'`).
 * Use everywhere a post's type is shown so feed cards, detail views and the composer stay consistent.
 */
export default function IdeaTypeBadge({
  ideaType,
  size = 'sm',
  onImage = false,
}: {
  ideaType?: 'SIGNAL' | 'ANALYSIS';
  size?: 'sm' | 'md';
  /** Set when the badge sits over an image/chart so it gets a blurred backdrop for legibility. */
  onImage?: boolean;
}) {
  const isAnalysis = ideaType === 'ANALYSIS';
  const dims = size === 'md'
    ? { cls: 'px-2.5 py-1 text-[11px] gap-1.5', icon: 13 }
    : { cls: 'px-2 py-0.5 text-[10px] gap-1', icon: 10 };

  const palette = isAnalysis
    ? { background: 'var(--win-muted)', color: 'var(--win)', border: '1px solid transparent' }
    : { background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' };

  return (
    <span
      className={`inline-flex items-center rounded-lg font-bold uppercase tracking-wide ${dims.cls}`}
      style={{ ...palette, ...(onImage ? { backdropFilter: 'blur(12px)' } : {}) }}
      title={isAnalysis
        ? 'Analysis — chart & thesis only, not counted in stats'
        : 'Signal — actionable trade with entry/SL/TP, counts toward stats'}
    >
      {isAnalysis ? <PenLine size={dims.icon} /> : <Zap size={dims.icon} />}
      {isAnalysis ? 'Analysis' : 'Signal'}
    </span>
  );
}
